class ComboBox extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.options = [];
    this.filteredOptions = [];
    this.highlightedIndex = -1;
    this.placeholder = this.getAttribute('data-placeholder');
    this.value = this.getAttribute('data-value');
    this.inputId = `combo-input-${Math.random().toString(36).slice(2)}`;
    this.listboxId = `combo-listbox-${Math.random().toString(36).slice(2)}`;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-block;
          position: relative;
          font-family: sans-serif;
        }
        ::slotted(input) {
          width: 100%;
          padding: 12px;
          box-sizing: border-box !important;
          background: white;
          font-size: 1.5rem;
        }
        ul {
          list-style: none;
          margin: 0;
          padding: 0;
          border: 1px solid #ccc;
          max-height: 150px;
          overflow-y: scroll;
          position: absolute;
          width: 100%;
          background: white;
          display: none;
          z-index: 100;
          border: 1px solid black;
          border-top: 0 none;
          box-sizing: border-box;
        }
        ul::-webkit-scrollbar-track {
          -webkit-box-shadow: none;
          background-color: #F5F5F5;
        }

        ul::-webkit-scrollbar {
          width: 4px;
          background-color: #F5F5F5;
        }

        ul::-webkit-scrollbar-thumb {
          background-color: #000000;
          border: 2px solid #555555;
        }
        li {
          padding: 8px;
          cursor: pointer;
          font-size: 1.5rem;
        }
        li.highlighted {
          background-color: #007bff;
          color: white;
        }
        li:hover {
          background: #eee;
        }
      </style>
      <slot name="input">
      </slot>
      <ul role="listbox"></ul>
    `;
  }

  connectedCallback() {
    this.input = this.querySelector('[slot="input"]');
    this.dropdown = this.shadowRoot.querySelector('ul');

    if (!this.input) {
      console.warn('combo-box requires an <input slot="input"> child.');
      return;
    }

    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-haspopup', 'listbox');
    this.input.setAttribute('aria-expanded', 'false');

    this.input.addEventListener('input', () => this.filterOptions());
    this.input.addEventListener('focus', () => this.showOptions());
    this.input.addEventListener('blur', () => setTimeout(() => this.hideOptions(), 200));
    this.input.addEventListener('keydown', (e) => this.handleKeyDown(e));

    this.dropdown.addEventListener('click', (e) => {
      if (e.target.tagName === 'LI') {
        const index = parseInt(e.target.getAttribute('data-index'), 10);
        const option = this.filteredOptions[index];
        this.selectOption(option);
      }
    });

    this.loadOptions();
  }

  loadOptions() {
    const data = JSON.parse(this.getAttribute('options'));
    this.options = data.map((opt) => (typeof opt === 'string' ? { label: opt, value: opt } : opt));
    this.filteredOptions = [...this.options];
    this.renderOptions();
  }

  filterOptions() {
    const value = this.input.value.toLowerCase();
    this.filteredOptions = this.options.filter((opt) => opt.label.toLowerCase().includes(value));
    this.renderOptions();
    this.showOptions();
  }

  renderOptions() {
    this.dropdown.innerHTML = '';
    this.highlightedIndex = this.options.findIndex((opt) => opt.label === this.input.value);
    this.filteredOptions.forEach((option, index) => {
      const li = document.createElement('li');
      li.textContent = option.label;
      li.setAttribute('data-index', index);
      li.setAttribute('role', 'option');
      li.setAttribute('part', 'option');
      li.id = `${this.listboxId}-option-${index}`;
      if (index === this.highlightedIndex) {
        li.classList.add('highlighted');
        this.input.setAttribute('aria-activedescendant', li.id);
      }
      this.dropdown.appendChild(li);
    });

    if (this.highlightedIndex === -1) {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  showOptions() {
    if (this.filteredOptions.length > 0) {
      this.dropdown.style.display = 'block';
      this.input.setAttribute('aria-expanded', 'true');
    }
  }

  hideOptions() {
    this.dropdown.style.display = 'none';
    this.input.setAttribute('aria-expanded', 'false');
  }

  selectOption(option) {
    this.input.value = option.label;
    this.hideOptions();
    this.dispatchEvent(new CustomEvent('select', { detail: { option }, bubbles: true }));
  }

  handleKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.highlightedIndex = (this.highlightedIndex + 1) % this.filteredOptions.length;
      this.renderOptions();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.highlightedIndex = (this.highlightedIndex - 1 + this.filteredOptions.length) % this.filteredOptions.length;
      this.renderOptions();
    } else if (e.key === 'Enter') {
      if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredOptions.length) {
        this.selectOption(this.filteredOptions[this.highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      this.hideOptions();
    }
  }
}

customElements.define('combo-box', ComboBox);
